/**
 * Голос сдвига кэфа и процента — тот же слот, что у «проверить вилку»,
 * без похода на страницы контор. Нет сдвига — нет строки. Смерть говорит
 * пелена, не эта фраза.
 *
 * Сравнивается не с прошлым кадром, а с точкой, от которой человек считает
 * (`Detail.tsx` держит её якорем). Разница дорогая: кэф редко прыгает разом,
 * он ползёт — 2.10 → 2.08 → 2.06 → 2.04. Прошлый кадр на этом пути каждый раз
 * показывает крошечный шаг и ни разу не говорит главного: с той цены, ради
 * которой человек открыл карточку, вилка уехала вдвое. Именно суммарный сдвиг
 * и просили с ленты (тестер, 04.09).
 */

export interface ShiftLeg {
  source: string;
  odds: string;
}

export interface ShiftSnap {
  odds: readonly ShiftLeg[];
  roiPct: number;
}

/** На сколько должен уехать процент, чтобы строка не мигала на округлении. */
const ROI_TICK = 0.05;

const oddsSaid = (raw: string): string => {
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) ? n.toFixed(2) : raw;
};

/** Процент голосом экрана: минус типографский, один знак. */
export const pctSaid = (value: number): string => {
  const sign = value < 0 ? '−' : '';
  return `${sign}${Math.abs(value).toFixed(1)}%`;
};

const plusSaid = (from: number, to: number): string => {
  const span = `${pctSaid(from)} → ${pctSaid(to)}`;
  return from >= 0 && to >= 0 ? `плюс ${span}` : span;
};

/**
 * Ноги, у которых сдвинулся кэф. Сравнение идёт по озвученному числу, а не по
 * пришедшей строке: `2.100` и `2.1001` — разные строки и один и тот же кэф на
 * экране, и на таком «сдвиге» строка писала «Фонбет 2.10 → 2.10».
 */
const movedLegs = (
  prev: readonly ShiftLeg[],
  next: readonly ShiftLeg[],
): { source: string; from: string; to: string }[] => {
  const was = new Map(prev.map((leg) => [leg.source, leg.odds]));
  const out: { source: string; from: string; to: string }[] = [];
  for (const leg of next) {
    const from = was.get(leg.source);
    if (from !== undefined && oddsSaid(from) !== oddsSaid(leg.odds)) {
      out.push({ source: leg.source, from, to: leg.odds });
    }
  }
  return out;
};

/**
 * Откуда считаем сдвиг. `bet` — деньги уже стоят на первой ноге, и точка
 * отсчёта у человека одна: цена, под которую он их отдал. Сказать это надо
 * словами — то же число без «с вашей ставки» читается как новость про ленту.
 */
export type ShiftSince = 'open' | 'bet';

/**
 * Одна фраза про сдвиг. Слова: вилка, контора, кэф, ставка.
 * «плюс» — только когда оба процента не отрицательные.
 */
export function shiftSay(
  prev: ShiftSnap,
  next: ShiftSnap,
  nameOf: (source: string) => string,
  since: ShiftSince = 'open',
): string | null {
  const moved = movedLegs(prev.odds, next.odds);
  const roiMoved = Math.abs(prev.roiPct - next.roiPct) >= ROI_TICK;
  if (moved.length === 0 && !roiMoved) return null;

  const from = since === 'bet' ? 'с вашей ставки ' : '';
  if (next.roiPct < 0) {
    return `${from}вилка в минусе: ${pctSaid(next.roiPct)}, вторая нога пересчитана`;
  }

  const change =
    moved.length > 0
      ? `${from}вилка изменилась: ${moved
          .map((leg) => `${nameOf(leg.source)} ${oddsSaid(leg.from)} → ${oddsSaid(leg.to)}`)
          .join(', ')}`
      : `${from}вилка изменилась`;
  return `${change}, ${plusSaid(prev.roiPct, next.roiPct)}`;
}

export const snapOf = (odds: readonly ShiftLeg[], roiPct: number): ShiftSnap => ({
  odds: odds.map((leg) => ({ source: leg.source, odds: leg.odds })),
  roiPct,
});
